import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { headCell } from '../components/DataTable'
import { StatusDot } from '../components/StatusDot'
import { Button, Card, Field, Input, Note, Skeleton, Tag } from '../components/ui'
import { ALL_GRANTS, GRANT_LABELS, api, type AdminUser, type Grant } from '../lib/api'
import { useSession } from '../lib/session'
import { ViewHeader } from '../shell/ViewHeader'

/**
 * `/admin/users` — everything `APPLICATION_SPEC.md` §3.1.2 lists: approve or
 * reject, invite, assign role, grant and revoke individually, disable and
 * re-enable, resend a link, and last login.
 *
 * **It never reveals or sets another user's password.** An Admin can only
 * trigger the reset flow — which is visible to the user, unlike knowing their
 * credentials would be.
 *
 * Granularity is **per dashboard, deliberately** — granting `channels` gives all
 * five charts. Per-chart scoping was never asked for and is not built.
 */
export function AdminUsers() {
  const { me } = useSession()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: api.adminUsers })

  function mutation<TArgs extends unknown[]>(
    action: (...args: TArgs) => Promise<unknown>,
    note?: (result: unknown) => string,
  ) {
    return useMutation({
      mutationFn: (args: TArgs) => action(...args),
      onSuccess: (result) => {
        setError(null)
        setMessage(note ? note(result) : null)
        void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      },
      onError: (cause: unknown) => {
        setMessage(null)
        setError(cause instanceof Error ? cause.message : 'That Did Not Work')
      },
    })
  }

  const approve = mutation<[number, Grant[]]>((id, grants) => api.approveUser(id, grants))
  const reject = mutation<[number]>((id) => api.rejectUser(id))
  const setGrants = mutation<[number, Grant[]]>((id, grants) => api.setGrants(id, grants))
  const setRole = mutation<[number, 'admin' | 'user']>((id, role) => api.setRole(id, role))
  const setStatus = mutation<[number, 'active' | 'disabled']>((id, s) => api.setStatus(id, s))
  const resend = mutation<[number]>(
    (id) => api.resendLink(id),
    (result) => `A ${(result as { kind: string }).kind} link has been sent.`,
  )

  const pending = (users.data ?? []).filter((user) => user.status === 'pending')
  const rest = (users.data ?? []).filter((user) => user.status !== 'pending')

  return (
    <div>
      {/* X-12. This page had no title at all, so its content started flush
          against the top of `main` while every other page began 168px down —
          the most visible instance of the inconsistency Dishant reported.
          `scoped={false}`: a channel and a date range mean nothing here. */}
      <ViewHeader
        title="Users"
        status={`${(users.data ?? []).length} accounts`}
        scoped={false}
      />
      <div className="flex flex-col gap-4">
        {error && <Note tone="error">{error}</Note>}
      {message && <Note tone="good">{message}</Note>}

      <InviteForm onError={setError} onDone={() => {
        setMessage('Invited. A Set-Password Link Has Been Sent.')
        void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      }} />

      <Card
        title={`Awaiting Approval${pending.length ? ` · ${pending.length}` : ''}`}
      >
        {users.isLoading && <Skeleton rows={2} />}
        {!users.isLoading && pending.length === 0 && (
          <p className="m-0 text-sm" style={{ color: 'var(--text-secondary)' }}>
            No One Is Waiting to Be Approved.
          </p>
        )}
        {pending.map((user) => (
          <PendingRow
            key={user.id}
            user={user}
            onApprove={(grants) => approve.mutate([user.id, grants])}
            onReject={() => reject.mutate([user.id])}
            onResend={() => resend.mutate([user.id])}
          />
        ))}
      </Card>

      <Card title={`Users${rest.length ? ` · ${rest.length}` : ''}`}>
        {users.isLoading && <Skeleton rows={4} />}
        {users.isError && (
          <Note tone="error">Could Not Load the User List.</Note>
        )}
        {!users.isLoading && rest.length === 0 && (
          <p className="m-0 text-sm" style={{ color: 'var(--text-secondary)' }}>
            No Accounts Yet.
          </p>
        )}
        {rest.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {['User', 'Status', 'Role', 'Access', 'Last Login', ''].map((heading) => (
                    <th key={heading} style={headCell}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rest.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={user.email === me?.email}
                    onGrants={(grants) => setGrants.mutate([user.id, grants])}
                    onRole={(role) => setRole.mutate([user.id, role])}
                    onStatus={(status) => setStatus.mutate([user.id, status])}
                    onResend={() => resend.mutate([user.id])}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        </Card>
      </div>
    </div>
  )
}

function GrantPicker({
  value,
  onChange,
}: {
  value: Grant[]
  onChange: (next: Grant[]) => void
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {ALL_GRANTS.map((grant) => (
        <label key={grant} className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={value.includes(grant)}
            onChange={(event) => {
              const next = event.target.checked
                ? [...value, grant]
                : value.filter((item) => item !== grant)
              onChange(next)
            }}
          />
          <span style={{ color: 'var(--text-secondary)' }}>{GRANT_LABELS[grant]}</span>
        </label>
      ))}
    </div>
  )
}

function PendingRow({
  user,
  onApprove,
  onReject,
  onResend,
}: {
  user: AdminUser
  onApprove: (grants: Grant[]) => void
  onReject: () => void
  onResend: () => void
}) {
  // Jesse's default: "give everyone access thanks, but not to the revenue
  // stuff still." So the three dashboards are pre-ticked and revenue is not.
  const [grants, setGrants] = useState<Grant[]>(['stories', 'spotlights', 'channels'])

  return (
    <div className="border-t py-3 first:border-t-0" style={{ borderColor: 'var(--border)' }}>
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium">{user.full_name || user.email}</span>
        {user.full_name && (
          <span className="text-xs" style={{ color: 'var(--text-label)' }}>{user.email}</span>
        )}
        {!user.email_verified && <Tag tone="warning">Email Not Confirmed</Tag>}
      </div>

      {user.email_verified ? (
        <>
          <div className="mb-3">
            <GrantPicker value={grants} onChange={setGrants} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => onApprove(grants)}>
              Approve with This Access
            </Button>
            <Button variant="danger" onClick={onReject}>Reject</Button>
          </div>
        </>
      ) : (
        <Button onClick={onResend}>Resend Confirmation</Button>
      )}
    </div>
  )
}

function UserRow({
  user,
  isSelf,
  onGrants,
  onRole,
  onStatus,
  onResend,
}: {
  user: AdminUser
  isSelf: boolean
  onGrants: (grants: Grant[]) => void
  onRole: (role: 'admin' | 'user') => void
  onStatus: (status: 'active' | 'disabled') => void
  onResend: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [grants, setGrants] = useState<Grant[]>(user.grants)

  const cell = { padding: '0.5rem', borderBottom: '1px solid var(--border)', verticalAlign: 'top' }

  return (
    <>
      <tr>
        <td style={cell}>
          <span className="block">{user.full_name || user.email}</span>
          {user.full_name && (
            <span className="text-xs" style={{ color: 'var(--text-label)' }}>{user.email}</span>
          )}
        </td>
        <td style={cell}>
          <StatusDot state={user.status === 'active' ? 'good' : 'idle'}>
            {user.status === 'active' ? 'Active' : 'Disabled'}
          </StatusDot>
          {!user.has_password && (
            <div className="mt-1"><Tag tone="warning">No Password Set</Tag></div>
          )}
        </td>
        <td style={cell}>
          <Tag tone={user.role === 'admin' ? 'brand' : 'neutral'}>
            {user.role === 'admin' ? 'Admin' : 'User'}
          </Tag>
        </td>
        <td style={cell}>
          {user.role === 'admin' ? (
            <span className="text-xs" style={{ color: 'var(--text-label)' }}>
              {/* An Admin implies every grant and is never gated on the grants
                  table — so showing grant rows here would be misleading. */}
              Everything, Including Revenue
            </span>
          ) : user.grants.length ? (
            <span className="flex flex-wrap gap-1">
              {user.grants.map((grant) => (
                <Tag key={grant}>{GRANT_LABELS[grant]}</Tag>
              ))}
            </span>
          ) : (
            <span className="text-xs" style={{ color: 'var(--text-label)' }}>No dashboards</span>
          )}
        </td>
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>
          <span className="is-num text-xs" style={{ color: 'var(--text-secondary)' }}>
            {user.last_login_at ? new Date(user.last_login_at).toISOString().slice(0, 10) : '—'}
          </span>
        </td>
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>
          <div className="flex flex-wrap justify-end gap-1">
            {user.role !== 'admin' && (
              <Button variant="quiet" onClick={() => setEditing((open) => !open)}>
                {editing ? 'Cancel' : 'Access'}
              </Button>
            )}
            <Button variant="quiet" onClick={onResend}>Resend Link</Button>
            {!isSelf && (
              <>
                <Button
                  variant="quiet"
                  onClick={() => onRole(user.role === 'admin' ? 'user' : 'admin')}
                >
                  {user.role === 'admin' ? 'Demote' : 'Make Admin'}
                </Button>
                <Button
                  variant="quiet"
                  onClick={() => onStatus(user.status === 'active' ? 'disabled' : 'active')}
                >
                  {user.status === 'active' ? 'Disable' : 'Re-enable'}
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={6} style={{ ...cell, background: 'var(--bg-page)' }}>
            <div className="mb-2">
              <GrantPicker value={grants} onChange={setGrants} />
            </div>
            <Button
              variant="primary"
              onClick={() => {
                onGrants(grants)
                setEditing(false)
              }}
            >
              Save Access
            </Button>
          </td>
        </tr>
      )}
    </>
  )
}

function InviteForm({
  onDone,
  onError,
}: {
  onDone: () => void
  onError: (message: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<'admin' | 'user'>('user')
  const [grants, setGrants] = useState<Grant[]>(['stories', 'spotlights', 'channels'])
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      await api.inviteUser(email, fullName || null, role, grants)
      setEmail('')
      setFullName('')
      onDone()
      setOpen(false)
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Could Not Send the Invite')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Invite Someone"
      action={
        <Button variant="quiet" onClick={() => setOpen((value) => !value)}>
          {open ? 'Close' : 'New Invite'}
        </Button>
      }
    >
      {open && (
        <form onSubmit={submit} noValidate>
          <div className="flex flex-wrap gap-4">
            <div style={{ minWidth: '16rem', flex: 1 }}>
              <Field label="Email">
                <Input type="email" value={email} required
                       onChange={(event) => setEmail(event.target.value)} />
              </Field>
            </div>
            <div style={{ minWidth: '12rem', flex: 1 }}>
              <Field label="Name" hint="Optional.">
                <Input value={fullName} onChange={(event) => setFullName(event.target.value)} />
              </Field>
            </div>
          </div>
          <Field label="Role">
            <div className="flex gap-4">
              {(['user', 'admin'] as const).map((option) => (
                <label key={option} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="role"
                    checked={role === option}
                    onChange={() => setRole(option)}
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {option === 'admin' ? 'Administrator' : 'User'}
                  </span>
                </label>
              ))}
            </div>
          </Field>
          {role === 'user' ? (
            <Field label="Access" hint="An Administrator Always Has Everything, Including Revenue.">
              <GrantPicker value={grants} onChange={setGrants} />
            </Field>
          ) : (
            <Note>
              An Administrator Can Do Everything, Including Viewing and Editing
              Revenue, and Managing Users.
            </Note>
          )}
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Sending…' : 'Send Invite'}
          </Button>
        </form>
      )}
    </Card>
  )
}
